import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { findProduct, useBag } from './BagContext';
import QtyStepper from './QtyStepper';
import useOverlay from './useOverlay';
import useOverlayMotion from './useOverlayMotion';

export default function BagDrawer({ onClose }: { onClose: () => void }) {
  const { lines, count, subtotal, setQty, remove } = useBag();
  const closeFn = useRef<() => void>(onClose);
  const panelRef = useOverlay(() => closeFn.current());
  const { backdropRef, requestClose } = useOverlayMotion('drawer', onClose, panelRef);
  closeFn.current = requestClose;

  return (
    <div className="ff-sans fixed inset-0 z-[100] flex justify-end text-[#111111]" onClick={requestClose}>
      <div ref={backdropRef} aria-hidden="true" className="absolute inset-0 bg-black/30"></div>
      <div
        ref={panelRef}
        data-lenis-prevent
        role="dialog"
        aria-modal="true"
        aria-label="Shopping bag"
        className="relative bg-white w-full sm:w-[420px] h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 md:px-8 h-16 border-b border-black/10">
          <span className="text-[13px] tracking-wide">Bag ({count})</span>
          <button type="button" data-autofocus onClick={requestClose} aria-label="Close bag" className="w-8 h-8 flex items-center justify-center cursor-pointer whitespace-nowrap hover:opacity-50">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {lines.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <p className="ff-serif italic text-[22px]">Your bag is empty.</p>
            <p className="text-[13px] text-black/50 mt-2">Nothing composed yet.</p>
            <Link to="/shop" onClick={onClose} className="mt-6 text-[13px] border-b border-black/30 hover:border-black pb-0.5 whitespace-nowrap cursor-pointer">Browse the Flower Edit →</Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto px-5 md:px-8 divide-y divide-black/10">
              {lines.map((l) => {
                const p = findProduct(l.id);
                if (!p) return null;
                return (
                  <li key={l.id} className="flex gap-4 py-5">
                    <div className="w-20 h-24 shrink-0">
                      <img src={p.image} alt={`${p.name} — Fleur & Form`} title={p.name} className="w-full h-full object-cover object-center" />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[15px] tracking-[-0.01em] truncate">{p.name}</span>
                        <span className="text-[13px] whitespace-nowrap">${p.price * l.qty}</span>
                      </div>
                      <span className="text-[12px] text-black/40 mt-1">${p.price} each</span>
                      <div className="mt-auto flex items-center justify-between pt-3">
                        <QtyStepper value={l.qty} onChange={(v) => setQty(l.id, v)} label={`Quantity for ${p.name}`} />
                        <button type="button" onClick={() => remove(l.id)} className="text-[12px] text-black/50 hover:text-black border-b border-transparent hover:border-black cursor-pointer whitespace-nowrap">Remove</button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-black/10 px-5 md:px-8 py-6">
              <div className="flex items-baseline justify-between text-[14px]">
                <span>Subtotal</span>
                <span className="tabular-nums">${subtotal}</span>
              </div>
              <p className="text-[12px] text-black/40 mt-1">Shipping calculated at checkout.</p>
              <div className="mt-5 h-12 flex items-center justify-center border border-dashed border-black/20 text-[12px] text-black/50 text-center px-3">
                Checkout is unavailable in this demo template.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}